import React from 'react';
import './PrivacyPolicy.css';

function Section({ title, children }) {
  return (
    <section className="privacy-section">
      <h2 className="privacy-section-title">{title}</h2>
      <div className="privacy-section-content">{children}</div>
    </section>
  );
}

export default function TermsOfService() {
  return (
    <main className="privacy-policy-main">
      <div className="privacy-policy-card">
        <header className="privacy-policy-header">
          <h1 className="privacy-policy-title">TERMS OF SERVICE</h1>
          <div className="privacy-policy-meta">
            Effective Date: March 4, 2026 <br />
            Last Updated: June 30, 2026
          </div>
        </header>

        <Section title="Introduction">
          <p>
            These Terms of Service (“Terms”) govern your access to and use of Instapaw’s mobile application, website, and related services. By creating an account, posting content, or using Instapaw in any way, you agree to be bound by these Terms.
          </p>
          <div className="privacy-section-note">Your access to Instapaw is conditional upon acceptance of these Terms; if you do not agree, you may not use Instapaw.</div>
        </Section>

        <Section title="1. Our Purpose and Role">
          <p>Instapaw is a digital community built to support animal welfare. We provide tools that allow users to:</p>
          <ul>
            <li>Report injured or distressed animals</li>
            <li>Connect with volunteers and rescue organizations</li>
            <li>Post animals for adoption</li>
            <li>Share information about missing animals</li>
            <li>Collaborate around lawful animal welfare initiatives</li>
            <li>Create or join Stray Network feeding areas and local animal-care circles</li>
            <li>Maintain community animal records, feeding logs, and care-status updates</li>
            <li>Share responder, NGO, foster, transporter, or service-area availability</li>
          </ul>
          <p>We operate the technology infrastructure that enables these interactions.</p>
          <div className="privacy-section-note">
            We do not operate rescue teams, veterinary clinics, adoption agencies, or emergency services. We do not physically intervene in animal situations, and we do not supervise user interactions outside Instapaw.
          </div>
        </Section>

        <Section title="2. What We Do Not Guarantee">
          <ul>
            <li>We do not guarantee that a rescuer will respond to a report.</li>
            <li>We do not guarantee that a feeder, NGO, veterinarian, foster, transporter, responder, or local coordinator will act on any request or alert.</li>
            <li>We do not guarantee response time.</li>
            <li>We do not verify the medical condition, vaccination status, sterilization status, or health history of animals unless a feature expressly marks a record as verified.</li>
            <li>We do not screen or certify adopters.</li>
            <li>We do not guarantee the truthfulness of every user submission.</li>
            <li>We do not oversee in-person meetings, feeding activity, animal handling, animal transport, local disputes, or animal transfers.</li>
          </ul>
          <div className="privacy-section-note">
            All real-world actions happen independently of Instapaw and are undertaken at users’ own discretion and responsibility.
          </div>
        </Section>

        <Section title="3. Eligibility">
          <p>To use Instapaw, you must:</p>
          <ul>
            <li>Be at least 18 years old (or have verifiable parental/guardian consent).</li>
            <li>Have the legal capacity to enter into binding agreements.</li>
            <li>Not be prohibited from using online services under applicable law.</li>
            <li>Not have previously been permanently removed from Instapaw.</li>
          </ul>
          <div className="privacy-section-note">
            We reserve the right to suspend or terminate accounts that do not meet eligibility standards.
          </div>
        </Section>

        <Section title="4. Account Responsibilities">
          <p>When creating an account, you agree to:</p>
          <ul>
            <li>Provide accurate and current information.</li>
            <li>Maintain the confidentiality of your login credentials.</li>
            <li>Be responsible for activity conducted through your account.</li>
            <li>Notify us promptly of unauthorized access.</li>
            <li>Creating multiple accounts to bypass restrictions is prohibited.</li>
          </ul>
        </Section>

        <Section title="5. User Content and Community Responsibility">
          <p>You are solely responsible for the content you post, including:</p>
          <ul>
            <li>Rescue reports</li>
            <li>Adoption listings</li>
            <li>Missing animal notices</li>
            <li>Photographs and videos</li>
            <li>Comments and messages</li>
            <li>Feeding areas, feeding points, and feeding logs</li>
            <li>Community animal profiles and care records</li>
            <li>Emergency alerts, responder areas, NGO areas, and volunteer role information</li>
          </ul>
          <p>By posting content, you confirm that:</p>
          <ul>
            <li>The information is truthful to the best of your knowledge.</li>
            <li>You have the right to share the content.</li>
            <li>You are not violating privacy or intellectual property rights.</li>
            <li>You are not impersonating another individual or organization.</li>
            <li>Any animal health, vaccination, sterilization, feeding, or emergency information you submit is accurate to the best of your knowledge.</li>
          </ul>
          <div className="privacy-section-note">
            Misleading or fabricated emergency reports undermine community trust and may result in permanent removal.
          </div>
        </Section>

        <Section title="6. Rescue Reports">
          <p>If you submit a rescue report:</p>
          <ul>
            <li>Provide accurate location details.</li>
            <li>Do not exaggerate urgency.</li>
            <li>Do not encourage trespassing on private property.</li>
            <li>Do not use Instapaw for pranks or testing.</li>
          </ul>
          <div className="privacy-section-note">
            Instapaw only distributes information. It does not dispatch emergency responders, verify responder availability, or guarantee rescue, feeding, transport, medical, or foster outcomes.
          </div>
        </Section>

        <Section title="7. Adoption and Rehoming">
          <p>Adoption posts are user-initiated listings. Instapaw:</p>
          <ul>
            <li>Does not perform background checks.</li>
            <li>Does not verify ownership claims.</li>
            <li>Does not guarantee the health or temperament of animals.</li>
            <li>Does not mediate private adoption disputes.</li>
            <li>Does not oversee financial transactions between users.</li>
          </ul>
          <div className="privacy-section-note">
            Users must conduct their own due diligence and ensure that transfers comply with applicable laws. Any adoption or rehoming arrangement is a private agreement between users.
          </div>
        </Section>

        <Section title="8. Missing Animal Reports">
          <p>By submitting a missing animal listing, you represent that:</p>
          <ul>
            <li>You are the lawful owner or authorized representative.</li>
            <li>The information is accurate.</li>
            <li>The report is made in good faith.</li>
          </ul>
          <div className="privacy-section-note">
            False reports may result in account suspension and possible legal consequences.
          </div>
        </Section>

        <Section title="9. Stray Network and Local Animal-Care Coordination">
          <p>Stray Network, feeding-area, community animal profile, health-status, responder-area, NGO-area, and emergency-alert features are community coordination tools.</p>
          <ul>
            <li>They are not official municipal, veterinary, rescue, law-enforcement, or emergency-service records.</li>
            <li>They may contain self-reported information from users.</li>
            <li>They should not be used as the sole source for urgent medical, legal, public-safety, or animal-control decisions.</li>
            <li>Users remain responsible for verifying information before acting on it.</li>
          </ul>
          <div className="privacy-section-note">
            Instapaw provides technology for coordination only. It does not manage local care circles, certify responders, or control offline actions.
          </div>
        </Section>

        <Section title="10. Feeding Areas, Local Rules, and Public Safety">
          <p>If you create, join, log, or act on a feeding area or feeding point, you agree to:</p>
          <ul>
            <li>Follow applicable animal welfare laws, municipal directions, society/RWA rules, and public-safety requirements.</li>
            <li>Avoid trespassing, obstruction, harassment, littering, unsafe feeding, or conflict escalation.</li>
            <li>Keep submitted feeding records, cleanliness notes, risk markers, and location details accurate.</li>
            <li>Respect lawful restrictions around schools, gates, roads, parks, private property, and other sensitive places.</li>
          </ul>
          <div className="privacy-section-note">
            Instapaw does not approve, designate, or enforce feeding points. Any offline feeding activity is performed at the user's own risk and responsibility.
          </div>
        </Section>

        <Section title="11. Animal Health and Care Records">
          <p>Vaccination, sterilization, deworming, injury, aggression, treatment, and other care records may be self-reported unless clearly marked as verified.</p>
          <ul>
            <li>Do not fabricate health status, proof photos, certificates, or treatment records.</li>
            <li>Do not misrepresent an animal as vaccinated, sterilized, treated, safe, aggressive, owned, abandoned, or available for adoption.</li>
            <li>Use qualified veterinary or municipal guidance where medical or public-health decisions are involved.</li>
          </ul>
          <div className="privacy-section-note">
            False or misleading animal-care records may result in content removal, account restriction, suspension, or termination.
          </div>
        </Section>

        <Section title="12. Admin Announcements and Operational Notices">
          <p>We may send or display admin announcements, safety notices, policy updates, feature notices, enforcement notices, or operational messages in the App.</p>
          <ul>
            <li>Announcements may be shown broadly or to relevant users based on app activity, account state, location-based features, or community-safety needs.</li>
            <li>Some announcements may require user action, acknowledgement, or dismissal.</li>
            <li>Announcements are informational and do not create a guarantee that any offline action will occur.</li>
          </ul>
        </Section>
        <Section title="13. Prohibited Conduct">
          <p>The following behavior is strictly prohibited:</p>
          <ul>
            <li>Submitting false or misleading reports</li>
            <li>Harassing or threatening other users</li>
            <li>Promoting animal cruelty or exploitation</li>
            <li>Facilitating illegal animal trade or trafficking</li>
            <li>Commercial breeding advertisements disguised as adoption</li>
            <li>Impersonation or identity fraud</li>
            <li>Sharing explicit or pornographic material</li>
            <li>Uploading graphic violent content unrelated to documentation</li>
            <li>Distributing malware or attempting unauthorized system access</li>
            <li>Sharing private personal data without consent</li>
            <li>Publishing or exposing another user&apos;s exact location, feeding routine, phone number, responder details, service area, or local-care group information without consent</li>
            <li>Using Stray Network information to harass, threaten, stalk, shame, target, or retaliate against feeders, responders, NGOs, adopters, or other users</li>
            <li>Exploiting minors in any form</li>
            <li>Engaging in or facilitating child sexual abuse or exploitation (CSAE), including sharing or soliciting child sexual abuse material (CSAM)</li>
          </ul>
          <div className="privacy-section-note">
            Severe violations, including CSAE/CSAM, will be acted upon immediately and may be reported to law enforcement and relevant authorities as required by law.
          </div>
        </Section>

        <Section title="14. Zero-Tolerance Policy">
          <div className="privacy-section-note">
            Instapaw has zero tolerance for objectionable content or abusive users. Users may not post, upload, share, or promote abusive, harassing, hateful, sexually explicit, exploitative, violent, fraudulent, illegal, or otherwise objectionable content.
            Users who violate this policy may have their content removed, their account restricted, suspended, or permanently terminated. Users can report objectionable content and block abusive users from within the app.
          </div>
        </Section>

        <Section title="15. Content Moderation & Enforcement">
          <p>To protect the integrity of the community, we may:</p>
          <ul>
            <li>Remove posts</li>
            <li>Restrict visibility of content</li>
            <li>Temporarily suspend accounts</li>
            <li>Permanently terminate accounts</li>
          </ul>
          <div className="privacy-section-note">
            Moderation decisions are made to protect users, animals, and legal compliance. We may act without prior notice where immediate removal is necessary.
          </div>
        </Section>

        <Section title="16. Reporting Violations">
          <p>Users may report:</p>
          <ul>
            <li>Fraudulent adoption listings</li>
            <li>False rescue reports</li>
            <li>Harassment</li>
            <li>Policy violations</li>
            <li>Intellectual property infringement</li>
            <li>Privacy violations</li>
            <li>Child sexual abuse or exploitation (CSAE/CSAM)</li>
          </ul>
          <div className="privacy-section-note">
            Reports of child sexual abuse or exploitation will be acted upon immediately and may be reported to law enforcement and relevant authorities as required by law. Other reports are reviewed internally. We are not obligated to disclose details of enforcement outcomes.
          </div>
        </Section>

        <Section title="17. Intellectual Property">
          <p>All software, branding, platform design, and proprietary materials belong to Instapaw or its licensors.</p>
          <ul>
            <li>You may not copy or replicate platform functionality</li>
            <li>You may not reverse-engineer the application</li>
            <li>You may not use branding without written permission</li>
          </ul>
          <p>You retain ownership of content you upload. However, by posting, you grant us a non-exclusive license to display and use that content for operating, improving, and protecting Instapaw.</p>
        </Section>

        <Section title="18. Offline Interactions and Assumption of Risk">
          <p>Any in-person meeting, feeding activity, rescue effort, emergency response, animal handling, animal transport, foster arrangement, veterinary visit, civic interaction, or adoption exchange takes place entirely outside Instapaw.</p>
          <p>By engaging in such activity, you accept full responsibility for associated risks, including:</p>
          <ul>
            <li>Personal injury</li>
            <li>Animal injury</li>
            <li>Property damage</li>
            <li>Financial disputes</li>
            <li>Resident, RWA, society, civic-body, or public-space disputes</li>
            <li>Animal bites, scratches, disease exposure, transport risks, or veterinary expenses</li>
            <li>Misrepresentation by other users</li>
          </ul>
          <div className="privacy-section-note">
            Instapaw does not supervise or control offline behavior.
          </div>
        </Section>

        <Section title="19. Service Availability">
          <p>We strive to maintain reliable service but do not guarantee uninterrupted access. Maintenance, technical failures, or security events may temporarily affect availability.</p>
        </Section>

        <Section title="20. Disclaimer of Warranties">
          <p>Instapaw is provided on an “as available” basis. We do not guarantee:</p>
          <ul>
            <li>Accuracy of user-generated content</li>
            <li>Accuracy of feeding-area, animal health, vaccination, sterilization, responder, NGO, or emergency-alert records</li>
            <li>Rescue outcomes</li>
            <li>Adoption success</li>
            <li>Continuous system security</li>
            <li>Fitness for a particular purpose</li>
          </ul>
          <div className="privacy-section-note">
            Use of Instapaw is at your own risk.
          </div>
        </Section>

        <Section title="21. Limitation of Liability">
          <p>To the fullest extent permitted by law, Instapaw and its operators shall not be liable for:</p>
          <ul>
            <li>Indirect or consequential damages</li>
            <li>Lost data or goodwill</li>
            <li>Failed rescue attempts</li>
            <li>Failed feeding, response, transport, foster, medical, or local-care coordination attempts</li>
            <li>Adoption disputes</li>
            <li>Injury to persons or animals</li>
            <li>Third-party misconduct</li>
            <li>Service interruptions</li>
          </ul>
          <div className="privacy-section-note">
            Where liability cannot be excluded, it shall be limited to the minimum amount permitted under applicable law.
          </div>
        </Section>

        <Section title="22. Indemnification">
          <p>You agree to assume responsibility for claims arising from:</p>
          <ul>
            <li>Your content</li>
            <li>Your violation of these Terms</li>
            <li>Your misuse of Instapaw</li>
            <li>Your participation in rescue, feeding, local-care, emergency-response, transport, foster, veterinary, civic, or adoption activities</li>
          </ul>
          <div className="privacy-section-note">
            You agree to defend and hold harmless Instapaw and its operators from resulting legal claims.
          </div>
        </Section>

        <Section title="23. Account Suspension and Termination">
          <p>We may suspend or permanently terminate accounts for:</p>
          <ul>
            <li>Repeated policy violations</li>
            <li>Fraudulent activity</li>
            <li>Harmful or exploitative behavior</li>
            <li>Misuse of Stray Network, feeding-area, health-record, responder, or emergency-alert features</li>
            <li>Attempts to bypass enforcement mechanisms</li>
            <li>Illegal conduct</li>
          </ul>
          <div className="privacy-section-note">
            Upon termination, access to Instapaw may be revoked and content removed. For information on how to request account deletion, see our <a href="/delete-account" style={{color: '#ecb003', textDecoration: 'underline'}}>Delete Account</a> page.
          </div>
        </Section>

        <Section title="24. Changes to These Terms">
          <p>We may update these Terms periodically. Continued use after updates constitutes acceptance of the revised Terms.</p>
        </Section>

        <Section title="25. Governing Law">
          <p>These Terms are governed by the laws of India (or your chosen jurisdiction). Legal disputes shall be subject to the competent courts of that jurisdiction.</p>
        </Section>

        <Section title="26. Contact Information">
          <p>For questions, grievances, or legal notices:</p>
          <div className="privacy-section-note">
            Instapaw<br/>
            {/* [Address]<br/> */}
            Email: <a href="mailto:instapaw.official@gmail.com" target="_blank" rel="noopener noreferrer">instapaw.official@gmail.com</a>
          </div>
          <p>We aim to acknowledge complaints within 72 working hours (or as mandated by applicable law) and resolve them within a reasonable timeframe.</p>
        </Section>

        <footer className="privacy-policy-footer">
          © {new Date().getFullYear()} Instapaw. All rights reserved.
        </footer>
      </div>
    </main>
  );
}
